import styled from '@emotion/styled'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useContext, useMemo } from 'react'
import { Card, Col, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import AdminContainer from '../../components/admin/AdminContainer'
import CoachMemberCard from '../../components/coach/CoachMemberCard'
import Loader from '../../components/global/Loader'
import SkillCard from '../../components/global/SkillCard'
import Title from '../../components/global/Title'
import { SERVER_URL, apiHeader } from '../../config'
import UserContext from '../../context/UserContext'
import { CoachModel } from '../../types/coach.type'
import { SkillStatusEnum } from '../../types/skill.type'
import { TrainingSessionGroup } from '../../types/training-session-group.type'
import { SkillsWrapper } from './my-members/CoachMyMembers'

const CoachDashboard = () => {
    const user = useContext(UserContext)
    const { t } = useTranslation('translation')

    const { data: coach } = useQuery({
        queryKey: ['coach-users'],
        queryFn: () =>
            axios
                .get<CoachModel>(`${SERVER_URL}/coachs/${user.coachData.id}`, apiHeader(user.tokenCoach))
                .then((res) => res.data),
    })

    const { data: ownedGroups = [] } = useQuery<TrainingSessionGroup[]>({
        queryKey: ['trainingSessionGroups', 'owned'],
        queryFn: async () => {
            const { data } = await axios.get(
                `${SERVER_URL}/training-session-groups?type=owned`,
                apiHeader(user.tokenCoach),
            )
            return data
        },
        enabled: !!user?.coachData?.id,
    })

    const { data: sharedGroups = [] } = useQuery<TrainingSessionGroup[]>({
        queryKey: ['trainingSessionGroups', 'shared'],
        queryFn: async () => {
            const { data } = await axios.get(
                `${SERVER_URL}/training-session-groups?type=shared`,
                apiHeader(user.tokenCoach),
            )
            return data
        },
        enabled: !!user?.coachData?.id,
    })

    const groups = useMemo(() => {
        const allGroups = [...ownedGroups, ...sharedGroups]
        const uniqueGroups = allGroups.filter(
            (group, index, self) => index === self.findIndex((g) => g.id === group.id),
        )
        return uniqueGroups
    }, [ownedGroups, sharedGroups])

    const groupsWithStats = useMemo(() => {
        if (!coach || !groups.length) return []

        return groups
            .filter((group) => group.title !== 'Aucun groupe' && group.members && group.members.length > 0)
            .map((group) => {
                const groupMembers = group.members!.map((member) => member.member)

                const groupSkills = groupMembers
                    .map((member) => member.skills)
                    .flat()
                    .reduce(
                        (acc, userSkill) => {
                            const existingSkill = acc.find((s) => s.name === userSkill.skill.name)
                            if (existingSkill) {
                                existingSkill.nombre++
                            } else {
                                acc.push({ id: userSkill.skill.id, name: userSkill.skill.name, nombre: 1 })
                            }
                            return acc
                        },
                        [] as { id: number; name: string; nombre: number }[],
                    )

                const playersWithMostMissings = groupMembers
                    .sort(
                        (user1, user2) =>
                            user2.skills.filter((skill) => skill.status === SkillStatusEnum.TO_DISCOVER).length -
                            user1.skills.filter((skill) => skill.status === SkillStatusEnum.TO_DISCOVER).length,
                    )
                    .slice(0, 4)

                const leastMasteredSkills = groupSkills.sort((a, b) => a.nombre - b.nombre).slice(0, 4)

                return {
                    ...group,
                    playersWithMostMissings,
                    leastMasteredSkills,
                }
            })
    }, [coach, groups])

    if (!user.coachData || !coach) return <Loader />

    return (
        <div>
            <Title>{t('coach.dashboard')}</Title>

            {groupsWithStats.length === 0 ? (
                <TextContainer>
                    <p>{t('coach.noGroupsWithMembers')}</p>
                </TextContainer>
            ) : (
                <Row className="g-4">
                    {groupsWithStats.map((group) => (
                        <Col key={group.id} xs={12} lg={12}>
                            <Card className="h-100">
                                <Card.Header
                                    style={{ backgroundColor: 'var(--primary-color, #B40000)', color: 'white' }}
                                >
                                    <Card.Title className="mb-0" style={{ fontSize: '1.6rem', fontWeight: '600' }}>
                                        {group.title}
                                    </Card.Title>
                                    {group.description && <small style={{ opacity: 0.9 }}>{group.description}</small>}
                                </Card.Header>
                                <Card.Body>
                                    <PlayersWrapper>
                                        <Title style={{ marginBottom: '16px' }}>
                                            {t('coach.4-players-with-most-missings')}
                                        </Title>
                                        <SkillsWrapper>
                                            {group.playersWithMostMissings.length > 0 ? (
                                                group.playersWithMostMissings.map((user) => (
                                                    <CoachMemberCard key={'CoachMemberCard-' + user.id} user={user} />
                                                ))
                                            ) : (
                                                <p>{t('coach.noPlayersWithMissings')}</p>
                                            )}
                                        </SkillsWrapper>
                                    </PlayersWrapper>

                                    <PlayersWrapper>
                                        <Title style={{ marginBottom: '16px' }}>
                                            {t('coach.skills-least-mastered')}
                                        </Title>
                                        <SkillsWrapper>
                                            {group.leastMasteredSkills.length > 0 ? (
                                                group.leastMasteredSkills.map((skill) => (
                                                    <SkillCard
                                                        key={'SkillCard-' + skill.name}
                                                        id={skill.id}
                                                        name={skill.name}
                                                        status={SkillStatusEnum.TO_DISCOVER}
                                                        isInWhiteBackground
                                                    />
                                                ))
                                            ) : (
                                                <p>{t('coach.noSkillsToDisplay')}</p>
                                            )}
                                        </SkillsWrapper>
                                    </PlayersWrapper>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
        </div>
    )
}

const PlayersWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 16px;
    border-radius: 6px;
    background-color: #f8f9fa;
    margin-bottom: 16px;
`

const TextContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 24px;
    border-radius: 6px;
    background-color: white;
    margin-bottom: 16px;
`

export default CoachDashboard
